import React, { useCallback, useEffect, useState } from 'react';
import { Linking, Pressable, Text, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import type { BranchSettingsScreenViewModel } from '../useBranchSettingsScreen';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { SettingsSaveCta, hubStyles } from './settingsHubUi';
import { PairPickupDisplayModal } from './PairPickupDisplayModal';
import { PickupDisplaySettingsModal } from './PickupDisplaySettingsModal';
import {
  deletePickupDisplay,
  listPickupDisplays,
  rotatePickupDisplayToken,
  type PickupDisplayRow,
} from '../../infra/pickupDisplayRepo';
import { ticketColors } from '../../../shared/theme/ticketColors';

const PORTAL_BASE = (process.env.EXPO_PUBLIC_CUSTOMER_PORTAL_URL ?? 'https://www.vuelto.ar').replace(/\/$/, '');

function recoveryUrl(path: string): string {
  return `${PORTAL_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

export function PickupDisplayCard({ vm }: { vm: BranchSettingsScreenViewModel }) {
  const hasPickupDisplay = useHasBusinessCapability('pickup_display');
  const hasKitchen = useHasBusinessCapability('kitchen_tickets');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const branchId = vm.currentBranch?.id ?? '';
  const [rows, setRows] = useState<PickupDisplayRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pairOpen, setPairOpen] = useState(false);
  const [settingsRow, setSettingsRow] = useState<PickupDisplayRow | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const canManage = vm.canEditPosRules && branchId;

  const reload = useCallback(async () => {
    if (!branchId || !hasPickupDisplay) return;
    setLoading(true);
    try {
      setRows(await listPickupDisplays(branchId));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, hasPickupDisplay]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!hasPickupDisplay) return null;
  if (!hasKitchen && !hasDelivery) return null;

  const copyRecovery = async (path: string) => {
    const url = recoveryUrl(path);
    await Clipboard.setStringAsync(url);
    setMessage('Link copiado. Pegalo en el navegador de la TV.');
  };

  const handleRotate = async (id: string) => {
    if (!canManage) return;
    try {
      const res = await rotatePickupDisplayToken(id);
      await copyRecovery(res.recovery_url_path);
      void reload();
    } catch {
      setMessage('No pudimos rotar el link.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!canManage) return;
    try {
      await deletePickupDisplay(id);
      void reload();
    } catch {
      setMessage('No pudimos desvincular la pantalla.');
    }
  };

  return (
    <>
      <PaperSheet padded testID="branch.pickup-display">
        <Text style={styles.cardTitle}>Pantalla de retiro</Text>
        <Text style={styles.captionBlock}>
          Para el mostrador o la cocina vista cliente. En la TV abrí {PORTAL_BASE.replace(/^https?:\/\//, '')}/retiro.
        </Text>

        {loading ? <Text style={hubStyles.hintSecondary}>Cargando…</Text> : null}

        {rows.map((row) => (
          <View
            key={row.id}
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: ticketColors.rule,
            }}
          >
            <Text style={styles.cardTitle}>{row.label}</Text>
            <Text style={hubStyles.hintSecondary}>
              Perfil: {row.profile === 'pickup' ? 'Retiro' : row.profile === 'delivery' ? 'Delivery' : 'Ambos'}
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              <SettingsSaveCta
                label="Ajustes"
                variant="secondary"
                layoutWide={false}
                disabled={!canManage}
                onPress={() => setSettingsRow(row)}
              />
              <SettingsSaveCta
                label="Copiar link"
                variant="secondary"
                layoutWide={false}
                disabled={!canManage}
                onPress={() => void copyRecovery(row.recovery_url_path)}
              />
              <SettingsSaveCta
                label="Rotar link"
                variant="secondary"
                layoutWide={false}
                disabled={!canManage}
                onPress={() => void handleRotate(row.id)}
              />
              <SettingsSaveCta
                label="Desvincular"
                variant="secondary"
                layoutWide={false}
                disabled={!canManage}
                onPress={() => void handleDelete(row.id)}
              />
            </View>
          </View>
        ))}

        {message ? <Text style={{ marginTop: 8, color: ticketColors.ok }}>{message}</Text> : null}

        <View style={{ marginTop: 16, flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <SettingsSaveCta
            label="Vincular pantalla"
            layoutWide={false}
            disabled={!canManage}
            onPress={() => setPairOpen(true)}
          />
          <Pressable onPress={() => void Linking.openURL(`${PORTAL_BASE}/retiro`)}>
            <Text style={{ color: ticketColors.brandPrimary, marginTop: 12 }}>Abrir /retiro en el navegador</Text>
          </Pressable>
        </View>
      </PaperSheet>

      <PairPickupDisplayModal
        visible={pairOpen}
        branchId={branchId}
        branchName={vm.currentBranch?.name ?? ''}
        onClose={() => setPairOpen(false)}
        onSuccess={() => {
          setMessage('Pantalla vinculada. La TV debería mostrar los pedidos.');
          void reload();
        }}
      />

      <PickupDisplaySettingsModal
        visible={settingsRow != null}
        row={settingsRow}
        onClose={() => setSettingsRow(null)}
        onSaved={() => {
          setMessage('Ajustes guardados.');
          void reload();
        }}
      />
    </>
  );
}
